/**
 * 压测用的假星海（?fake=10000）：不连服务，没有照片，只有星。
 * 分布模仿真实相册：大多数日子没有照片，周末多一点，纪念日和旅行扎堆。
 */
import { LIMITS, addDays, daysBetween, localDateOf, parseLocalDate, type LocalDate, type MemberDto, type MemoryDto, type PhotoDto, type Shine, type SnapshotResponse } from '@star-sea/shared';

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hsl(h: number, s: number, l: number): string {
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const c = l - s * Math.min(l, 1 - l) * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/** 回忆主题：色相范围，模仿照片的主色 */
const THEMES: { hue: [number, number]; title: string[] }[] = [
  { hue: [12, 38], title: ['海边日落', '晚霞', '黄昏散步'] },
  { hue: [190, 215], title: ['看海', '湖边', '雨天'] },
  { hue: [330, 355], title: ['樱花', '生日', '花市'] },
  { hue: [95, 140], title: ['爬山', '野餐', '植物园'] },
  { hue: [255, 285], title: ['夜景', '演唱会', '烟花'] },
  { hue: [40, 55], title: ['秋天', '银杏', '咖啡馆'] },
];

const TRIPS = ['去大理', '京都', '海岛', '回老家', '露营', '冰岛', '成都', '厦门'];

export function fakeSnapshot(count: number, seed = 520): SnapshotResponse {
  const rnd = mulberry32(seed);
  const since: LocalDate = '2022-05-20';
  const today = localDateOf(new Date());
  const span = Math.max(1, daysBetween(since, today));
  const members: MemberDto[] = [
    { id: 'me', displayName: '我', tint: 'gold' },
    { id: 'ta', displayName: 'Ta', tint: 'rose' },
  ];

  // 每一天的权重
  const weight = new Float64Array(span + 1);
  const trip = new Int16Array(span + 1).fill(-1);
  for (let d = 0; d <= span; d++) {
    const date = addDays(since, d);
    const { m, d: day } = parseLocalDate(date);
    const weekday = new Date(`${date}T12:00:00`).getDay();
    let w = rnd() < 0.38 ? 0.3 + Math.pow(rnd(), 2.5) * 6 : 0;
    if (weekday === 0 || weekday === 6) w *= 1.8;
    if ((m === 5 && day === 20) || (m === 2 && day === 14) || (m === 12 && (day === 24 || day === 25))) w += 25 + rnd() * 20;
    weight[d] = w;
  }
  // 每年四五次旅行，每次 3–7 天，照片很多
  const trips = Math.round((span / 365) * 4.5);
  for (let k = 0; k < trips; k++) {
    const start = Math.floor(rnd() * (span - 8)), len = 3 + Math.floor(rnd() * 5);
    for (let d = start; d < start + len && d <= span; d++) {
      weight[d] += 18 + rnd() * 40;
      trip[d] = k;
    }
  }

  let total = 0;
  for (const w of weight) total += w;
  const counts = new Int32Array(span + 1);
  const frac: { d: number; f: number }[] = [];
  let assigned = 0;
  for (let d = 0; d <= span; d++) {
    const exact = (weight[d]! / total) * count;
    counts[d] = Math.floor(exact);
    assigned += counts[d]!;
    if (exact > 0) frac.push({ d, f: exact - counts[d]! });
  }
  frac.sort((a, b) => b.f - a.f);
  for (let k = 0; assigned < count && frac.length; k = (k + 1) % frac.length) {
    counts[frac[k]!.d]!++;
    assigned++;
  }

  const memories: MemoryDto[] = [];
  const photos: PhotoDto[] = [];
  let n = 0;
  for (let d = 0; d <= span; d++) {
    const c = counts[d]!;
    if (!c) continue;
    const date = addDays(since, d);
    const theme = THEMES[Math.floor(rnd() * THEMES.length)]!;
    const by = rnd() < 0.5 ? 'me' : 'ta';
    const shine = (c > 40 ? 5 : c > 12 ? 4 : c > 3 ? 3 : c > 1 ? 2 : 1) as Shine;
    const titled = trip[d]! >= 0 || c > 20 || rnd() < 0.06;
    const memory: MemoryDto = {
      id: `m${d}`,
      date,
      title: titled ? (trip[d]! >= 0 ? TRIPS[trip[d]! % TRIPS.length] : theme.title[Math.floor(rnd() * theme.title.length)]) : undefined,
      story: titled && rnd() < 0.5 ? '这是压测用的示例回忆，没有真实照片。' : undefined,
      shine,
      createdBy: by,
      createdAt: `${date}T12:00:00.000Z`,
      updatedAt: `${date}T12:00:00.000Z`,
      updatedBy: by,
    };
    memories.push(memory);
    const [h0, h1] = theme.hue;
    for (let i = 0; i < c; i++) {
      const portrait = rnd() < 0.55;
      photos.push({
        id: `f${n}`,
        memoryId: memory.id,
        date,
        width: portrait ? 3024 : 4032,
        height: portrait ? 4032 : 3024,
        color: hsl(h0 + rnd() * (h1 - h0), 0.35 + rnd() * 0.4, 0.35 + rnd() * 0.35),
        thumbs: false,
        mime: 'image/jpeg',
        bytes: 2_000_000 + Math.floor(rnd() * 3_000_000),
        createdBy: rnd() < 0.85 ? by : by === 'me' ? 'ta' : 'me',
        createdAt: new Date(Date.parse(`${date}T12:00:00.000Z`) + i * 1000).toISOString(),
      });
      n++;
    }
  }
  return { seq: 0, space: { id: 'fake', name: '压测星海', since, introHold: LIMITS.introHold }, members, memories, photos };
}
